import React, { useState, useEffect, useCallback, useRef } from "react";
import { useScrollToTop } from "../../../hooks/useScrollToTop";
import {
  Card,
  Typography,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  message,
  Popconfirm,
  Tooltip,
  Alert,
  Divider,
  InputNumber,
  Tag,
  Tabs,
  Row,
  Col,
  Collapse,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  ImportOutlined,
  CopyOutlined,
  CalculatorOutlined,
  WarningOutlined,
  UploadOutlined,
  HomeOutlined,
  SettingOutlined,
  ExportOutlined,
  DownloadOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useTender } from "../../../contexts/TenderContext";
import { useAutoSave } from "../../../hooks/useAutoSave";
import MainLayout from "../../layout/MainLayout";
import { PlotData, FoundationType } from "../../../types/tender.types";
import { COLORS } from "../../../utils/constants";
import {
  exportToExcel,
  importFromExcel,
  generateTemplate,
  validateImportedData,
  ExcelColumn,
} from "../../../utils/excelUtils";
import {
  calculatePlotArea,
  calculatePlotValues,
} from "../../../utils/plotCalculations";

const { Title, Text } = Typography;

// Note: calculatePlotArea and calculatePlotValues are now imported from shared utility

// Excel column definitions for export/import
const excelColumns: ExcelColumn[] = [
  { key: "plotNumber", title: "Plot Number" },
  { key: "plotName", title: "Plot Code" },
  { key: "houseType", title: "House Type" },
  { key: "foundationType", title: "Foundation Type" },
  { key: "typeSelection", title: "Type Selection" },
  {
    key: "heaveConditions",
    title: "Heave Conditions",
    render: (value) => (value ? "Yes" : "No"),
  },
  {
    key: "plotArea",
    title: "Plot Area (m²)",
    render: (value) => Number(value).toFixed(2),
  },
  {
    key: "existingGroundLevel",
    title: "EGL (m)",
    render: (value) => Number(value).toFixed(2),
  },
  {
    key: "stripStandardValue",
    title: "Strip Value",
    render: (value) => Number(value).toFixed(2),
  },
  {
    key: "finishedFloorLevel",
    title: "FFL (m)",
    render: (value) => Number(value).toFixed(2),
  },
  {
    key: "standardValueHouse",
    title: "Standard Value House",
    render: (value) => Number(value).toFixed(2),
  },
  {
    key: "standardValueGarage",
    title: "Standard Value Garage",
    render: (value) => Number(value).toFixed(2),
  },
  {
    key: "foundingDepthSchedule",
    title: "Founding Depth Schedule",
    render: (value) => (value ? Number(value).toFixed(2) : ""),
  },
  {
    key: "trenchfillFoundsStandardValue",
    title: "Trenchfill Founds Standard Value",
    render: (value) => (value ? Number(value).toFixed(2) : ""),
  },
  {
    key: "piledFoundsStandardValue",
    title: "Piled Founds Standard Value",
    render: (value) => (value ? Number(value).toFixed(2) : ""),
  },
  {
    key: "standardValueAdditionalBlocks",
    title: "Standard Value for Additional Blocks",
    render: (value) => (value ? Number(value).toFixed(2) : ""),
  },
  {
    key: "standardValueAdditionalConcrete",
    title: "Standard Value for Additional Concrete",
    render: (value) => (value ? Number(value).toFixed(2) : ""),
  },
  {
    key: "strippedLevel",
    title: "Less Strip (calculated)",
    render: (value) => Number(value).toFixed(2),
  },
  {
    key: "formationLevel",
    title: "Formation Level (calculated)",
    render: (value) => Number(value).toFixed(2),
  },
  {
    key: "excavationDepth",
    title: "Depth (calculated)",
    render: (value) => Number(value).toFixed(2),
  },
  {
    key: "foundationDepth",
    title: "Foundation Depth (calculated)",
    render: (value) => Number(value).toFixed(2),
  },
  {
    key: "calculatedTrenchfillFounds",
    title: "Trenchfill Founds (calculated)",
    render: (value) => (value ? Number(value).toFixed(2) : ""),
  },
  {
    key: "calculatedPiledFounds",
    title: "Piled Founds (calculated)",
    render: (value) => (value ? Number(value).toFixed(2) : ""),
  },
  {
    key: "additionalDepth",
    title: "Additional Depth (calculated)",
    render: (value) => (value ? Number(value).toFixed(2) : ""),
  },
  {
    key: "additionalBlocks",
    title: "Additional Blocks",
    render: (value) => (value ? Number(value).toFixed(2) : ""),
  },
  {
    key: "additionalConcrete",
    title: "Additional Concrete (calculated)",
    render: (value) => (value ? Number(value).toFixed(2) : ""),
  },
  {
    key: "cutVolume",
    title: "Cut Volume (m³)",
    render: (value) => Number(value).toFixed(2),
  },
  {
    key: "fillVolume",
    title: "Fill Volume (m³)",
    render: (value) => Number(value).toFixed(2),
  },
];

export const handleDownloadTemplate = async () => {
  await generateTemplate(
    `Plot_Measure_Template_${new Date().toISOString().split("T")[0]}`,
    excelColumns // Include all fields including calculated ones for reference
    // sampleData
  );
  message.success("Template downloaded successfully");
};

const PlotMeasure: React.FC = () => {
  useScrollToTop({ behavior: "smooth", delay: 100 });

  const { state, updateField, saveTender } = useTender();
  useAutoSave();

  // Use context plots directly instead of local state
  const plots = state.groundworks?.plots || [];

  useEffect(() => {
    console.log(
      "Current plots in context:",
      plots.map((p) => ({ id: p.id, plotNumber: p.plotNumber }))
    );
  }, [plots]);

  // Watch for changes in foundation wall measurements and recalculate plot areas
  useEffect(() => {
    const shouldRecalculate = plots.some((plot) => {
      const hasWallData =
        plot.externalWalls ||
        plot.partyWalls ||
        plot.garageExternalWalls ||
        plot.piledFoundation?.piledWalls ||
        plot.floorTypes;
      const currentCalculatedArea = calculatePlotArea(plot);
      const storedArea = plot.plotArea || 0;

      // Recalculate if there's wall data and the calculated area differs significantly from stored area
      return hasWallData && Math.abs(currentCalculatedArea - storedArea) > 0.01;
    });

    if (shouldRecalculate) {
      console.log(
        "Foundation wall measurements detected, recalculating plot areas..."
      );

      const recalculatedPlots = plots.map((plot) => {
        const recalculated = calculatePlotValues(plot);
        return {
          ...plot,
          ...recalculated,
          lastModified: new Date(),
          version: (plot.version || 0) + 1,
        };
      });

      // Update context with recalculated plot areas
      updateField("groundworks", {
        ...state.groundworks,
        plots: recalculatedPlots,
      });

      message.success(
        "Plot areas updated based on foundation wall measurements"
      );
    }
  }, [plots, updateField, state.groundworks]);
  const [editingPlot, setEditingPlot] = useState<PlotData | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [isManualMode, setIsManualMode] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [form] = Form.useForm();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // No need for auto-save effects since we're updating context directly

  const createNewPlot = (): PlotData => ({
    id: `plot_${Date.now()}`,
    plotNumber: "",
    plotName: "",
    houseType: "",
    foundationType: "trenchfill",
    plotArea: 0, // Will be calculated from foundation wall measurements
    typeSelection: "House",

    // Levels
    existingGroundLevel: 0,
    stripStandardValue: 0.334,
    finishedFloorLevel: 0,
    foundingLevel: 0, // Will be calculated as FFL - 0.9 if not provided
    standardValueHouse: 0.625,
    standardValueGarage: 0.475,
    foundingDepthSchedule: 0,

    // Foundation type specific standard values
    trenchfillFoundsStandardValue: 1.175,
    piledFoundsStandardValue: 1.225,

    // Calculated levels
    strippedLevel: 0,
    formationLevel: 0,
    excavationDepth: 0,
    foundationDepth: 0,

    // Foundation type specific calculated values
    calculatedTrenchfillFounds: 0,
    calculatedPiledFounds: 0,

    // Enhanced wall measurements
    externalWalls: {
      width900mm: 0,
      width800mm: 0,
      width750mm: 0,
      width600mm: 0,
      width450mm: 0,
    },
    partyWalls: {
      width1200mm: 0,
      width1100mm: 0,
      width1000mm: 0,
      width900mm: 0,
      width750mm: 0,
      width600mm: 0,
    },
    internalWalls: {
      width750mm: 0,
      width600mm: 0,
      width450mm: 0,
    },
    garageExternalWalls: {
      width900mm: 0,
      width750mm: 0,
      width600mm: 0,
      width450mm: 0,
    },
    garageInternalWalls: {
      width600mm: 0,
      width450mm: 0,
    },

    // Floor data
    floorArea: 0,
    floorType: "beam_block",
    floorTypes: {
      beamBlock: { plotFloors: 0, garageFloor: 0 },
      insitu: { plotFloors: 0, garageFloor: 0, topMesh: 0 },
    },

    // Cut/Fill calculations
    cutVolume: 0,
    fillVolume: 0,

    // Additional calculations
    additionalDepth: 0,
    additionalBlocks: 0,
    additionalConcrete: 0,
    standardValueAdditionalBlocks: 0.675,
    standardValueAdditionalConcrete: 0.5,

    // Piled foundation data
    piledFoundation: {
      topOfBeamAsPiledGA: 0,
      masonryHeight: 0,
      standardConcreteDepth: 0.5,
      numberOfPiles: 0,
      rebar: 0,
      piledWalls: {
        external1100mm: 0,
        external500mm: 0,
        external450mm: 0,
        party750mm: 0,
        party500mm: 0,
        party450mm: 0,
        intWall500mm: 0,
        tieBeam400x500: 0,
        garExt500mm: 0,
        garExt450mm: 0,
        garInt500mm: 0,
        garInt450mm: 0,
      },
    },

    // Tanking and Claymaster
    tanking: { linM: 0, below: 0, above: 0, total: 0 },
    trenchfillClaymaster: { linM: 0, depth: 0, m2: 0 },
    piledClaymaster: { linM: 0, depth: 0, m2: 0 },

    // Additional calculations
    scaffoldPlatform: 0,
    pileMatt: 0,

    // Existing fields
    minimumDepth: 0.9,
    additionalDepthRequired: 0,
    heaveConditions: false,
    heaveRequired: {},
    scaffoldLength: 0,
    isTerraced: false, // Default to detached for scaffold calculations
    isHighCut: false,
    isHighFill: false,
    hasUnusualValues: false,
    isManualEntry: true, // Internal flag, will not be exported
    dataSource: "manual",
    lastModified: new Date(),
    version: 1,
  });

  const handleAddPlot = () => {
    const newPlot = createNewPlot();
    // Remove ID for new plots - it will be generated on save
    const { id, ...newPlotWithoutId } = newPlot;
    setEditingPlot(null); // No editing plot for new entries

    // Calculate values and set form fields including calculated fields
    const calculatedValues = calculatePlotValues(newPlotWithoutId);
    form.setFieldsValue({ ...newPlotWithoutId, ...calculatedValues });
    setIsModalVisible(true);
  };

  const handleEditPlot = (plot: PlotData) => {
    console.log("Editing plot:", { id: plot.id, plotNumber: plot.plotNumber });
    setEditingPlot({ ...plot }); // Create a copy to avoid mutations

    // Calculate values and set form fields including calculated fields
    const calculatedValues = calculatePlotValues(plot);
    form.setFieldsValue({ ...plot, ...calculatedValues });
    setIsModalVisible(true);
  };

  const handleDeletePlot = (plotId: string) => {
    console.log("Deleting plot with ID:", plotId);
    console.log(
      "Current plots:",
      plots.map((p) => ({ id: p.id, plotNumber: p.plotNumber }))
    );

    const plotToDelete = plots.find((p) => p.id === plotId);
    if (!plotToDelete) {
      message.error("Plot not found");
      return;
    }

    const newPlots = plots.filter((p) => p.id !== plotId);
    console.log(
      "New plots after delete:",
      newPlots.map((p) => ({ id: p.id, plotNumber: p.plotNumber }))
    );

    // Update context directly
    updateField("groundworks", {
      ...state.groundworks,
      plots: newPlots,
    });

    // Force immediate save to ensure deletion persists
    setTimeout(() => {
      saveTender();
      console.log("Plot deletion saved to storage");
    }, 100);

    message.success(`Plot ${plotToDelete.plotNumber} deleted successfully`);
  };

  const handleSavePlot = useCallback(() => {
    // Get all form values, not just validated ones
    const allFormValues = form.getFieldsValue();
    console.log("=== ALL FORM VALUES ===");
    console.log("All form values:", allFormValues);
    console.log(
      "standardValueAdditionalBlocks in all values:",
      allFormValues.standardValueAdditionalBlocks
    );
    console.log(
      "standardValueAdditionalConcrete in all values:",
      allFormValues.standardValueAdditionalConcrete
    );
    console.log(
      "heaveConditions in all values:",
      allFormValues.heaveConditions
    );

    form
      .validateFields()
      .then((values) => {
        console.log("=== VALIDATED FORM VALUES ===");
        console.log(
          "standardValueAdditionalBlocks:",
          values.standardValueAdditionalBlocks
        );
        console.log(
          "standardValueAdditionalConcrete:",
          values.standardValueAdditionalConcrete
        );
        console.log("heaveConditions:", values.heaveConditions);

        // Use all form values instead of just validated ones to ensure we get all fields
        const finalValues = { ...allFormValues, ...values };

        const calculatedValues = calculatePlotValues(finalValues);

        let newPlots: PlotData[];

        // Check if we're editing (finalValues.id exists) or adding new
        const existingPlot = finalValues.id
          ? plots.find((p) => p.id === finalValues.id)
          : null;
        const isEditing = !!existingPlot;

        console.log("Existing plot found:", existingPlot);
        console.log("Is editing:", isEditing);

        if (isEditing) {
          console.log("UPDATING existing plot with ID:", finalValues.id);
          // Update existing plot
          const updatedPlot: PlotData = {
            ...finalValues, // Form values already include the ID
            ...calculatedValues,
            lastModified: new Date(),
            version: (existingPlot.version || 0) + 1,
          };
          console.log("Updated plot:", {
            id: updatedPlot.id,
            plotNumber: updatedPlot.plotNumber,
            standardValueAdditionalBlocks:
              updatedPlot.standardValueAdditionalBlocks,
            standardValueAdditionalConcrete:
              updatedPlot.standardValueAdditionalConcrete,
            heaveConditions: updatedPlot.heaveConditions,
          });
          newPlots = plots.map((p) => {
            if (p.id === finalValues.id) {
              console.log("Replacing plot:", p.id, "with:", updatedPlot.id);
              return updatedPlot;
            }
            return p;
          });
          message.success(
            `Plot ${updatedPlot.plotNumber} updated successfully`
          );
        } else {
          console.log("ADDING new plot");
          // Add new plot with fresh ID
          const newId = `plot_${Date.now()}_${Math.random()
            .toString(36)
            .substr(2, 9)}`;
          const newPlot: PlotData = {
            ...finalValues,
            ...calculatedValues,
            id: newId,
            lastModified: new Date(),
            version: 1,
          };
          console.log("New plot created:", {
            id: newPlot.id,
            plotNumber: newPlot.plotNumber,
            standardValueAdditionalBlocks:
              newPlot.standardValueAdditionalBlocks,
            standardValueAdditionalConcrete:
              newPlot.standardValueAdditionalConcrete,
            heaveConditions: newPlot.heaveConditions,
          });
          newPlots = [...plots, newPlot];
          message.success(`Plot ${newPlot.plotNumber} added successfully`);
        }

        console.log(
          "Final plots array:",
          newPlots.map((p) => ({ id: p.id, plotNumber: p.plotNumber }))
        );
        console.log("=== END DEBUG ===");

        // Update context directly
        updateField("groundworks", {
          ...state.groundworks,
          plots: newPlots,
        });

        setIsModalVisible(false);
        setEditingPlot(null);
        form.resetFields();
      })
      .catch((error) => {
        console.error("Validation failed:", error);
        message.error("Please check all required fields");
      });
  }, [form, editingPlot, plots, updateField, state.groundworks]);

  const handleRecalculateAll = () => {
    const recalculatedPlots = plots.map((plot) => ({
      ...plot,
      ...calculatePlotValues(plot),
      lastModified: new Date(),
      version: (plot.version || 0) + 1,
    }));

    // Update context directly
    updateField("groundworks", {
      ...state.groundworks,
      plots: recalculatedPlots,
    });

    message.success(
      `Recalculated ${plots.length} plots - plot areas updated from foundation wall measurements`
    );
  };

  const columns: ColumnsType<PlotData> = [
    {
      title: "Plot No.",
      dataIndex: "plotNumber",
      key: "plotNumber",
      width: 110,
      sorter: (a, b) => a.plotNumber.localeCompare(b.plotNumber),
    },
    {
      title: "House Type",
      dataIndex: "houseType",
      key: "houseType",
      width: 120,
    },
    {
      title: "Foundation",
      dataIndex: "foundationType",
      key: "foundationType",
      width: 110,
      render: (type: FoundationType) => (
        <Tag
          color={
            type === "trenchfill"
              ? "blue"
              : type === "piled"
              ? "red"
              : type === "vibro"
              ? "orange"
              : "green"
          }
        >
          {type.charAt(0).toUpperCase() + type.slice(1)}
        </Tag>
      ),
    },
    {
      title: "EGL (m)",
      dataIndex: "existingGroundLevel",
      key: "existingGroundLevel",
      width: 110,
      render: (value: number) => value?.toFixed(2),
    },
    {
      title: "FFL (m)",
      dataIndex: "finishedFloorLevel",
      key: "finishedFloorLevel",
      width: 110,
      render: (value: number) => value?.toFixed(2),
    },
    {
      title: "Formation (m)",
      dataIndex: "formationLevel",
      key: "formationLevel",
      width: 110,
      render: (value: number) => (
        <span
          style={{
            backgroundColor: "#f5f5f5",
            padding: "2px 4px",
            borderRadius: "2px",
          }}
        >
          {value?.toFixed(2)}
        </span>
      ),
    },
    {
      title: "Depth (m)",
      dataIndex: "excavationDepth",
      key: "excavationDepth",
      width: 120,
      render: (value: number) => (
        <span
          style={{
            backgroundColor: "#f5f5f5",
            padding: "2px 4px",
            borderRadius: "2px",
            color: value < 0 ? "#ff4d4f" : value > 2 ? "#fa8c16" : "inherit",
          }}
        >
          {value?.toFixed(2)}
        </span>
      ),
    },

    {
      title: "Conditions",
      key: "conditions",
      width: 120,
      render: (_, record) => (
        <div>
          {record.heaveConditions && <Tag color="orange">Heave</Tag>}
          {record.additionalDepthRequired > 0 && (
            <Tag color="purple">Add. Depth</Tag>
          )}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 110,
      render: (_, record) => (
        <Space size="small">
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditPlot(record)}
          />
          <Popconfirm
            title="Delete this plot?"
            onConfirm={() => handleDeletePlot(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
          {record.hasUnusualValues && (
            <Tooltip title="Values flagged for review">
              <WarningOutlined style={{ color: "#fa8c16" }} />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  // Export functions
  const handleExportExcel = () => {
    exportToExcel({
      filename: `Plot_Measure_${new Date().toISOString().split("T")[0]}`,
      sheetName: "Plot Data",
      columns: excelColumns,
      data: plots,
    });
    message.success("Plot data exported to Excel successfully");
  };

  // Import function
  const handleImportExcel = async (file: File) => {
    setImportLoading(true);
    try {
      const result = await importFromExcel<Partial<PlotData>>(file, {
        expectedColumns: excelColumns.map((col) => col.title),
        headerRow: 0,
      });

      if (result.errors.length > 0) {
        message.error(`Import failed: ${result.errors.join(", ")}`);
        setImportLoading(false);
        return;
      }

      if (result.warnings.length > 0) {
        message.warning(`Import warnings: ${result.warnings.join(", ")}`);
      }

      // Validate and convert imported data
      const validationRules = {
        "Plot Number": { required: true, type: "string" as const },
        "Plot Name": { type: "string" as const },
        "House Type": { required: true, type: "string" as const },
        "Foundation Type": {
          required: true,
          type: "string" as const,
          validator: (value: string) =>
            ["trenchfill", "piled", "vibro", "raft"].includes(
              value.toLowerCase()
            ),
          errorMessage:
            "Foundation Type must be one of: trenchfill, piled, vibro, raft",
        },
        "Type Selection": {
          required: true,
          type: "string" as const,
          validator: (value: string) => ["House", "Garage"].includes(value),
          errorMessage: "Type Selection must be either House or Garage",
        },
        "Plot Area (m²)": { required: true, type: "number" as const },
        "EGL (m)": { required: true, type: "number" as const },
        "Strip Value": { required: true, type: "number" as const },
        "FFL (m)": { required: true, type: "number" as const },
        "Standard Value House": { required: true, type: "number" as const },
        "Standard Value Garage": { required: true, type: "number" as const },
        "Founding Depth Schedule": { type: "number" as const },
        "Trenchfill Founds Standard Value": { type: "number" as const },
        "Piled Founds Standard Value": { type: "number" as const },
        "Standard Value for Additional Blocks": { type: "number" as const },
        "Standard Value for Additional Concrete": { type: "number" as const },
        "Additional Depth (calculated)": { type: "number" as const },
        "Additional Blocks": { type: "number" as const },
        "Additional Concrete (calculated)": { type: "number" as const },
        "Heave Conditions": {
          type: "boolean" as const,
          validator: (value: any) => {
            const strValue = String(value).toLowerCase();
            return (
              ["yes", "no", "true", "false", "1", "0"].includes(strValue) ||
              typeof value === "boolean"
            );
          },
          errorMessage: "Heave Conditions must be Yes/No, True/False, or 1/0",
        },
      };

      const { validData, errors } = validateImportedData<Partial<PlotData>>(
        result.data,
        validationRules
      );

      if (errors.length > 0) {
        const errorMessage = errors
          .map((e) => `Row ${e.row}: ${e.message}`)
          .join("\n");
        message.error(`Validation errors:\n${errorMessage}`);
        setImportLoading(false);
        return;
      }

      // Convert and process imported data
      const importedPlots: PlotData[] = validData.map((item, index) => {
        console.log(`Processing imported plot ${index + 1}:`, item);

        const newPlot: PlotData = {
          ...createNewPlot(),
          id: `imported_${Date.now()}_${index}`,
          plotNumber: item["Plot Number"] || "",
          plotName: item["Plot Name"] || "",
          houseType: item["House Type"] || "",
          foundationType: (item["Foundation Type"]?.toLowerCase() ||
            "trenchfill") as FoundationType,
          typeSelection: (item["Type Selection"] || "House") as
            | "House"
            | "Garage",
          plotArea: 0, // Will be calculated from foundation wall measurements, ignoring imported value
          existingGroundLevel: Number(item["EGL (m)"]) || 0,
          stripStandardValue: Number(item["Strip Value"]) || 0.3,
          finishedFloorLevel: Number(item["FFL (m)"]) || 0,
          standardValueHouse: Number(item["Standard Value House"]) || 0.225,
          standardValueGarage: Number(item["Standard Value Garage"]) || 0.15,
          foundingDepthSchedule: item["Founding Depth Schedule"]
            ? Number(item["Founding Depth Schedule"])
            : 0,
          trenchfillFoundsStandardValue: item[
            "Trenchfill Founds Standard Value"
          ]
            ? Number(item["Trenchfill Founds Standard Value"])
            : 0,
          piledFoundsStandardValue: item["Piled Founds Standard Value"]
            ? Number(item["Piled Founds Standard Value"])
            : 0,
          standardValueAdditionalBlocks: item[
            "Standard Value for Additional Blocks"
          ]
            ? Number(item["Standard Value for Additional Blocks"])
            : 0,
          standardValueAdditionalConcrete: item[
            "Standard Value for Additional Concrete"
          ]
            ? Number(item["Standard Value for Additional Concrete"])
            : 0,
          additionalBlocks: item["Additional Blocks"]
            ? Number(item["Additional Blocks"])
            : 0,
          // Note: calculated fields will be recalculated after import
          additionalDepth: item["Additional Depth (calculated)"]
            ? Number(item["Additional Depth (calculated)"])
            : 0,
          additionalConcrete: item["Additional Concrete (calculated)"]
            ? Number(item["Additional Concrete (calculated)"])
            : 0,
          heaveConditions: (() => {
            const heaveValue = item["Heave Conditions"];
            console.log(`Processing heaveConditions for plot ${index + 1}:`, {
              rawValue: heaveValue,
              type: typeof heaveValue,
              item: item,
            });

            if (typeof heaveValue === "boolean") {
              console.log(
                `Plot ${index + 1}: heaveConditions boolean value:`,
                heaveValue
              );
              return heaveValue;
            }
            if (typeof heaveValue === "string") {
              const lowerValue = heaveValue.toLowerCase().trim();
              const result =
                lowerValue === "yes" ||
                lowerValue === "true" ||
                lowerValue === "1";
              console.log(
                `Plot ${
                  index + 1
                }: heaveConditions string "${heaveValue}" -> ${result}`
              );
              return result;
            }
            if (typeof heaveValue === "number") {
              const result = heaveValue === 1;
              console.log(
                `Plot ${
                  index + 1
                }: heaveConditions number ${heaveValue} -> ${result}`
              );
              return result;
            }
            const fallback = item.heaveConditions === true || false;
            console.log(
              `Plot ${index + 1}: heaveConditions fallback -> ${fallback}`
            );
            return fallback;
          })(),
          // Set reasonable defaults for fields that are no longer imported
          foundingLevel: 0, // Will be calculated as FFL - 0.9 during calculations
          isTerraced: false, // Default to detached house
          isManualEntry: false, // Imported data is not manual
          dataSource: "csv_import",
          lastModified: new Date(),
          version: 1,
        };

        // Calculate values for imported plot
        return { ...newPlot, ...calculatePlotValues(newPlot) };
      });

      // Add imported plots to existing plots
      const newPlots = [...plots, ...importedPlots];
      updateField("groundworks", {
        ...state.groundworks,
        plots: newPlots,
      });

      const heaveConditionsCount = importedPlots.filter(
        (p) => p.heaveConditions
      ).length;
      message.success(
        `Successfully imported ${importedPlots.length} plots` +
          (heaveConditionsCount > 0
            ? ` (${heaveConditionsCount} with heave conditions)`
            : "")
      );
      closeImportModal();
    } catch (error) {
      message.error(
        `Import failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    } finally {
      setImportLoading(false);
    }
  };

  // Helper function to close import modal and reset file input
  const closeImportModal = () => {
    setImportModalVisible(false);
    // Reset the file input to allow importing the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <MainLayout canProceed={plots.length > 0}>
      <div className="max-w-full ">
        {/* Main Table */}
        {/* <Card className="mb-6"> */}
        <div className="flex justify-between items-center mt-4">
          <Title level={4} className="mb-0">
            Plot Measure
          </Title>
          <Space>
            <Button
              icon={<ImportOutlined />}
              onClick={() => setImportModalVisible(true)}
            >
              Import Take-off
            </Button>
            <Button
              icon={<ExportOutlined />}
              onClick={handleExportExcel}
              disabled={plots.length === 0}
            >
              Export Excel
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddPlot}
            >
              Add Plot
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={plots}
          rowKey="id"
          pagination={false}
          scroll={{ x: 1200 }}
          size="small"
          className=""
        />
        {/* </Card> */}

        {/* Enhanced Plot Edit Modal */}
        <Modal
          title={editingPlot?.id ? "Edit Plot" : "Add New Plot"}
          open={isModalVisible}
          onOk={handleSavePlot}
          onCancel={() => {
            setIsModalVisible(false);
            setEditingPlot(null);
            form.resetFields();
          }}
          width={1200}
          okText="Save Plot"
        >
          <Form
            form={form}
            layout="vertical"
            onValuesChange={(changedValues, allValues) => {
              // Real-time calculation as user types
              const calculated = calculatePlotValues(allValues);
              // Only update calculated fields to avoid interfering with user input
              const calculatedFields = {
                plotArea: calculated.plotArea,
                strippedLevel: calculated.strippedLevel,
                formationLevel: calculated.formationLevel,
                excavationDepth: calculated.excavationDepth,
                foundationDepth: calculated.foundationDepth,
                cutVolume: calculated.cutVolume,
                fillVolume: calculated.fillVolume,
                calculatedTrenchfillFounds:
                  calculated.calculatedTrenchfillFounds,
                calculatedPiledFounds: calculated.calculatedPiledFounds,
                additionalDepth: calculated.additionalDepth,
                additionalConcrete: calculated.additionalConcrete,
              };
              form.setFieldsValue(calculatedFields);
            }}
          >
            {/* Hidden ID field to persist through form validation */}
            <Form.Item name="id" hidden>
              <Input type="hidden" />
            </Form.Item>

            {/* Simplified form without tabs - only Basic Info and Levels & Calculations */}
            <div>
              {/* Basic Info Section */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Form.Item
                    name="plotNumber"
                    label="Plot Number"
                    rules={[
                      { required: true, message: "Plot number is required" },
                    ]}
                  >
                    <Input placeholder="e.g., 1, 101, A1" />
                  </Form.Item>

                  <Form.Item name="plotName" label="Plot Code">
                    <Input placeholder="Optional plot code" />
                  </Form.Item>

                  <Form.Item
                    name="houseType"
                    label="House Type"
                    rules={[
                      { required: true, message: "House type is required" },
                    ]}
                  >
                    <Input placeholder="e.g., Ashford, Buckingham" />
                  </Form.Item>

                  <Form.Item
                    name="plotArea"
                    label={
                      <Tooltip title="Calculated from foundation wall measurements: (Sum of Plot Floors + Garage Floors) + (External walls × 0.2) + (Party walls + Garage walls × 0.1). For piled foundations, uses piledFoundation.piledWalls measurements.">
                        Plot Area (m²) - auto-calculated{" "}
                        <WarningOutlined style={{ color: "#1890ff" }} />
                      </Tooltip>
                    }
                  >
                    <InputNumber
                      disabled
                      step={0.1}
                      style={{ width: "100%", backgroundColor: "#f0f9ff" }}
                    />
                  </Form.Item>

                  <Form.Item
                    name="foundationType"
                    label="Foundation Type"
                    rules={[{ required: true }]}
                  >
                    <Select>
                      <Select.Option value="trenchfill">
                        Trenchfill
                      </Select.Option>
                      <Select.Option value="piled">Piled</Select.Option>
                      <Select.Option value="vibro">Vibro</Select.Option>
                      <Select.Option value="raft">Raft</Select.Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="typeSelection"
                    label="Type Selection"
                    rules={[{ required: true }]}
                  >
                    <Select>
                      <Select.Option value="House">House</Select.Option>
                      <Select.Option value="Garage">Garage</Select.Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="heaveConditions"
                    label={
                      <Tooltip title="Check if this plot has heave conditions requiring special foundation treatment. When enabled, all additional depth will be concrete instead of blockwork.">
                        Heave Conditions{" "}
                        <WarningOutlined style={{ color: "#ff7a00" }} />
                      </Tooltip>
                    }
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </div>
              </div>

              <Divider />

              {/* Levels & Calculations Section */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">
                  Levels & Calculations
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Form.Item
                    name="existingGroundLevel"
                    label="EGL as Schedule (m)"
                    rules={[{ required: true, type: "number" }]}
                  >
                    <InputNumber
                      step={0.001}
                      precision={3}
                      style={{ width: "100%" }}
                      placeholder="e.g., 125.500"
                    />
                  </Form.Item>

                  <Form.Item
                    name="stripStandardValue"
                    label="Strip Value (Standard)"
                    rules={[{ required: true, type: "number" }]}
                  >
                    <InputNumber
                      step={0.01}
                      precision={3}
                      style={{ width: "100%" }}
                      placeholder="0.00"
                    />
                  </Form.Item>

                  <Form.Item
                    name="finishedFloorLevel"
                    label="FFL (Finished Floor Level)"
                    rules={[{ required: true, type: "number" }]}
                  >
                    <InputNumber
                      step={0.001}
                      precision={3}
                      style={{ width: "100%" }}
                      placeholder="e.g., 125.800"
                    />
                  </Form.Item>

                  <Form.Item dependencies={["typeSelection"]}>
                    {({ getFieldValue }) => {
                      const typeSelection = getFieldValue("typeSelection");
                      return typeSelection === "House" ? (
                        <Form.Item
                          name="standardValueHouse"
                          label="Standard Value for House"
                          rules={[{ required: true, type: "number" }]}
                        >
                          <InputNumber
                            step={0.01}
                            precision={3}
                            style={{ width: "100%" }}
                            placeholder="typically 0.225"
                          />
                        </Form.Item>
                      ) : (
                        <Form.Item
                          name="standardValueGarage"
                          label="Standard Value for Garage"
                          rules={[{ required: true, type: "number" }]}
                        >
                          <InputNumber
                            step={0.01}
                            precision={3}
                            style={{ width: "100%" }}
                            placeholder="typically 0.15"
                          />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>

                  <Form.Item dependencies={["foundationType"]}>
                    {({ getFieldValue }) => {
                      const foundationType = getFieldValue("foundationType");
                      return foundationType === "trenchfill" ? (
                        <Form.Item
                          name="foundingDepthSchedule"
                          label="Founding Depth as Schedule"
                        >
                          <InputNumber
                            step={0.01}
                            precision={3}
                            style={{ width: "100%" }}
                          />
                        </Form.Item>
                      ) : null;
                    }}
                  </Form.Item>

                  <Form.Item dependencies={["foundationType"]}>
                    {({ getFieldValue }) => {
                      const foundationType = getFieldValue("foundationType");
                      let fieldLabel = "";
                      let fieldName = "";

                      if (foundationType === "trenchfill") {
                        fieldLabel = "Trenchfill Founds Standard Value";
                        fieldName = "trenchfillFoundsStandardValue";
                      } else if (foundationType === "piled") {
                        fieldLabel = "Piled Founds Standard Value";
                        fieldName = "piledFoundsStandardValue";
                      }

                      return foundationType === "trenchfill" ||
                        foundationType === "piled" ? (
                        <Form.Item name={fieldName} label={fieldLabel}>
                          <InputNumber
                            step={0.01}
                            precision={3}
                            style={{ width: "100%" }}
                            placeholder="Enter standard value"
                          />
                        </Form.Item>
                      ) : null;
                    }}
                  </Form.Item>

                  <Form.Item
                    name="standardValueAdditionalBlocks"
                    label="Standard Value for Additional Blocks (Used in Tanking Calculations)"
                  >
                    <InputNumber
                      step={0.01}
                      precision={3}
                      style={{ width: "100%" }}
                      placeholder="Enter additional blocks value"
                    />
                  </Form.Item>

                  <Form.Item
                    name="standardValueAdditionalConcrete"
                    label="Standard Value for Additional Concrete  (Used in Claymaster Calculations)"
                  >
                    <InputNumber
                      step={0.01}
                      precision={3}
                      style={{ width: "100%" }}
                      placeholder="Enter additional concrete value"
                    />
                  </Form.Item>
                  <Form.Item dependencies={["foundationType"]}>
                    {({ getFieldValue }) => {
                      const foundationType = getFieldValue("foundationType");
                      return foundationType === "trenchfill" ? (
                        <Form.Item
                          name="additionalBlocks"
                          label="Additional Blocks"
                          rules={[{ type: "number", min: 0 }]}
                        >
                          <InputNumber
                            step={0.01}
                            precision={3}
                            style={{ width: "100%" }}
                            placeholder="Enter additional blocks value"
                          />
                        </Form.Item>
                      ) : null;
                    }}
                  </Form.Item>
                </div>

                <h4 className="text-md font-semibold mb-3 mt-6">
                  Calculated Values
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Form.Item
                    name="strippedLevel"
                    label="Less Strip (calculated)"
                  >
                    <InputNumber
                      disabled
                      step={0.001}
                      precision={3}
                      style={{ width: "100%", backgroundColor: "#f0f9ff" }}
                    />
                  </Form.Item>

                  <Form.Item
                    name="formationLevel"
                    label="Formation Level (calculated)"
                  >
                    <InputNumber
                      disabled
                      step={0.001}
                      precision={3}
                      style={{ width: "100%", backgroundColor: "#f0f9ff" }}
                    />
                  </Form.Item>

                  <Form.Item name="excavationDepth" label="Depth (calculated)">
                    <InputNumber
                      disabled
                      step={0.001}
                      precision={3}
                      style={{ width: "100%", backgroundColor: "#f0f9ff" }}
                    />
                  </Form.Item>

                  <Form.Item dependencies={["foundationType"]}>
                    {({ getFieldValue }) => {
                      const foundationType = getFieldValue("foundationType");
                      let fieldLabel = "";
                      let fieldName = "";

                      if (foundationType === "trenchfill") {
                        fieldLabel = "Trenchfill Founds (calculated)";
                        fieldName = "calculatedTrenchfillFounds";
                      } else if (foundationType === "piled") {
                        fieldLabel = "Piled Founds (calculated)";
                        fieldName = "calculatedPiledFounds";
                      }

                      return foundationType === "trenchfill" ||
                        foundationType === "piled" ? (
                        <Form.Item name={fieldName} label={fieldLabel}>
                          <InputNumber
                            disabled
                            step={0.001}
                            precision={3}
                            style={{
                              width: "100%",
                              backgroundColor: "#f0f9ff",
                            }}
                          />
                        </Form.Item>
                      ) : null;
                    }}
                  </Form.Item>

                  <Form.Item dependencies={["foundationType"]}>
                    {({ getFieldValue }) => {
                      const foundationType = getFieldValue("foundationType");
                      return foundationType === "trenchfill" ? (
                        <Form.Item
                          name="additionalDepth"
                          label="Additional Depth (calc)"
                        >
                          <InputNumber
                            disabled
                            step={0.001}
                            precision={3}
                            style={{
                              width: "100%",
                              backgroundColor: "#f0f9ff",
                            }}
                          />
                        </Form.Item>
                      ) : null;
                    }}
                  </Form.Item>
                  <Form.Item dependencies={["foundationType"]}>
                    {({ getFieldValue }) => {
                      const foundationType = getFieldValue("foundationType");
                      return foundationType === "trenchfill" ? (
                        <Form.Item
                          name="additionalConcrete"
                          label="Additional Concrete (calc)"
                        >
                          <InputNumber
                            disabled
                            step={0.001}
                            precision={3}
                            style={{
                              width: "100%",
                              backgroundColor: "#f0f9ff",
                            }}
                          />
                        </Form.Item>
                      ) : null;
                    }}
                  </Form.Item>
                </div>
              </div>
            </div>
          </Form>
        </Modal>

        {/* Import Modal */}
        <Modal
          title="Import Plots from Excel"
          open={importModalVisible}
          onCancel={closeImportModal}
          footer={null}
          width={600}
        >
          <div className="space-y-4">
            <Alert
              message="Import Instructions"
              description={
                <div>
                  <p>
                    1. Download the template Excel file using the "Download
                    Template" button
                  </p>
                  <p>2. Fill in your plot data following the template format</p>
                  <p>3. Upload the completed Excel file below</p>
                  <p>
                    <strong>Note:</strong> Calculated fields will be
                    automatically computed after import
                  </p>
                </div>
              }
              type="info"
              showIcon
              className="mb-4"
            />

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleImportExcel(file);
                    // Reset the input value to allow importing the same file again
                    e.target.value = "";
                  }
                }}
                style={{ display: "none" }}
                id="excel-upload"
              />
              <label htmlFor="excel-upload" className="cursor-pointer">
                <UploadOutlined className="text-4xl text-gray-400 mb-4" />
                <div className="text-lg font-medium">
                  Click to upload Excel file
                </div>
                <div className="text-gray-500">or drag and drop</div>
                <div className="text-sm text-gray-400 mt-2">
                  Supports .xlsx and .xls files
                </div>
              </label>
            </div>

            {importLoading && (
              <div className="text-center py-4">
                <Text>Processing Excel file...</Text>
              </div>
            )}

            <div className="flex justify-end">
              {/* <Button
                icon={<FileExcelOutlined />}
                onClick={handleDownloadTemplate}
              >
                Download Template
              </Button> */}
              <Button onClick={closeImportModal}>Cancel</Button>
            </div>
          </div>
        </Modal>

        <style>{`
          .plot-measure-table .ant-table-thead > tr > th {
          min-width: 110px !important;
            background-color: ${COLORS.tableHeaderBg};
            font-weight: 600;
            font-size: 12px;
          }
          
          .plot-measure-table .ant-table-tbody > tr:hover > td {
            background-color: #f0f9ff;
          }
        `}</style>
      </div>
    </MainLayout>
  );
};

export default PlotMeasure;
